import { Flex } from 'components/Flex';
import { BKJButton, BKJIconTooltip, BKJPillDropdown } from '@bookjane2/bookjane-design-library';
import { FC, useMemo, MouseEventHandler, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import isUserPrimarySelector from 'store/selectors/userSelectors/isUserPrimarySelector';
import {
  H3,
  TieringDetailsTableContentCell,
  TieringRulesTableHeaderDivider,
  TieringDetailsSubHeader,
  ConflictingShiftsBiddingSubHeader,
  ConflictingShiftsBiddingHeading,
  ConflictingShiftsBiddingText,
  TieringDetailsBody,
  TieringDetailsWrapper,
  TieringDetailsTableRow,
  TieringDetailsTableHeaderCell,
  BiddingRuleDetails,
  BiddingRuleContainer,
  ShiftCreationToolTipText,
} from 'pages/Settings/modals/ShiftDistributionRulesTieringModal/ShiftDistributionRulesTieringModal.styled';
import {
  ShiftDistributionLocationDataProvider,
  useShiftDistributionLocationDataContext,
} from 'pages/Settings/modals/ShiftDistributionRulesTieringModal/views/ShiftDistributionLocation.data.context';
import {
  ShiftBiddingDetailsFormProvider,
  useShiftBiddingDetailsFormContext,
} from 'pages/Settings/modals/ShiftDistributionRulesTieringModal/views/ShiftBiddingDetails.form.context';
import { useTranslation } from 'hooks/useTranslation';
import {
  getBiddingPeriodTime,
  getBiddingRuleTime,
} from 'pages/Settings/modals/ShiftDistributionRulesTieringModal/views/ShiftBiddingDetails.constants';
import { BiddingTime } from 'types/graphql';

const ShiftBiddingDetailsViewComponent: FC = () => {
  const { locationName, biddingTimes, biddingConfiguration } =
    useShiftDistributionLocationDataContext();
  const { isEditMode, setIsEditMode, values, onChange, onSubmit, isSubmitting } =
    useShiftBiddingDetailsFormContext();

  const { t } = useTranslation();

  const canEditDefaultOvertime = useSelector(isUserPrimarySelector);

  const employmentStatusesDropdownItems = useMemo(
    () =>
      ['casual', 'full_time', 'part_time', 'flex'].map((status) => ({
        id: status,
        key: status,
        label: t(`status:${status.toUpperCase()}`),
        value: status,
      })),
    [t],
  );

  const BiddingRules = useMemo(() => {
    if (!biddingTimes) {
      return null;
    }
    return biddingTimes.map((rule: BiddingTime, index: number) => {
      const isGrey = index % 2 === 0;

      const biddingTimesLength = biddingTimes.length;
      const isLastTwoLeadTimeIdentical =
        biddingTimesLength >= 2 &&
        biddingTimes[biddingTimesLength - 1].leadTime ===
          biddingTimes[biddingTimesLength - 2].leadTime &&
        index === biddingTimesLength - 1;

      return (
        <TieringDetailsTableRow>
          <TieringDetailsTableContentCell isGrey={isGrey}>
            <Flex flexDirection="column">
              <div>{t('location_settings:RULE', { value: index + 1 })}</div>
              {isLastTwoLeadTimeIdentical ? (
                <BiddingRuleDetails>
                  {t('location_settings:BIDDING_RULE_MAX_DAYS', {
                    days: getBiddingRuleTime(rule),
                  })}
                </BiddingRuleDetails>
              ) : (
                <BiddingRuleDetails>
                  {index === 0 ? `0` : `${getBiddingRuleTime(biddingTimes[index - 1])} `} -&nbsp;
                  {`${getBiddingRuleTime(rule)} `}
                  {t('location_settings:BEFORE_SHIFT_START')}
                </BiddingRuleDetails>
              )}
            </Flex>
          </TieringDetailsTableContentCell>
          <TieringDetailsTableContentCell isGrey={isGrey}>
            {getBiddingPeriodTime(rule)}
          </TieringDetailsTableContentCell>
        </TieringDetailsTableRow>
      );
    });
  }, [biddingTimes, t]);

  if (!BiddingRules) return null;

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
    >
      <TieringDetailsWrapper>
        <H3>{locationName}</H3>
        <Flex flexDirection="column" width="100%">
          <TieringDetailsSubHeader>
            {t('location_settings:SHIFT_BIDDING_RULES')}
          </TieringDetailsSubHeader>
          <TieringDetailsBody>
            {t('location_settings:SHIFT_BIDDING_DESCRIPTION')}
          </TieringDetailsBody>
        </Flex>
        <Flex flexDirection="column" padding="30px 0 0 0">
          <TieringDetailsTableRow>
            <TieringDetailsTableHeaderCell>
              <Flex padding="0 0 0 10px">{t('location_settings:SHIFT_CREATION')}&nbsp;</Flex>
              <BKJIconTooltip iconName="Info" placement="top">
                <Flex flexDirection="column" width="258px">
                  <ShiftCreationToolTipText>
                    {t('location_settings:SHIFT_CREATION_TOOLTIP')}
                  </ShiftCreationToolTipText>
                </Flex>
              </BKJIconTooltip>
            </TieringDetailsTableHeaderCell>
            <TieringDetailsTableHeaderCell>
              {t('location_settings:BIDDING_PERIOD')}
            </TieringDetailsTableHeaderCell>
          </TieringDetailsTableRow>
          <TieringRulesTableHeaderDivider />
          <BiddingRuleContainer>{BiddingRules}</BiddingRuleContainer>
        </Flex>
        <Flex flexDirection="column" padding="40px 0 0 0">
          <ConflictingShiftsBiddingSubHeader>
            <ConflictingShiftsBiddingHeading>
              {t('location_settings:CONFLICTING_SHIFTS_BIDDING')}
            </ConflictingShiftsBiddingHeading>
          </ConflictingShiftsBiddingSubHeader>
          <Flex padding="0 0 24px 0">
            <ConflictingShiftsBiddingText>
              {t(
                `location_settings:${
                  isEditMode ? 'SELECT_EMPLOYMENT_TYPE' : 'CONFLICTING_SHIFTS_BIDDING_DESCRIPTION'
                }`,
              )}
              &nbsp;
            </ConflictingShiftsBiddingText>
            <Flex width="24px">
              <BKJIconTooltip iconName="Info" placement="top">
                <Flex flexDirection="column" width="258px">
                  <ShiftCreationToolTipText>
                    {t('location_settings:CONFLICTING_SHIFTS_BIDDING_TOOLTIP')}
                  </ShiftCreationToolTipText>
                </Flex>
              </BKJIconTooltip>
            </Flex>
          </Flex>
          {isEditMode ? (
            <Flex flexDirection="column" gap="8px" margin="0 0 32px 0">
              <div>{t('location_settings:EMPLOYMENT_TYPE')}</div>
              <BKJPillDropdown
                name="employmentStatuses"
                options={employmentStatusesDropdownItems}
                value={values.employmentStatuses}
                onChange={onChange}
                validations={[]}
                type="multi"
                max={4}
                placeholder="Select Employment Type"
              />
            </Flex>
          ) : (
            <TieringDetailsBody>
              {biddingConfiguration?.conflictEmploymentStatuses?.length
                ? biddingConfiguration.conflictEmploymentStatuses
                    .map((status: string) => t(`status:${status.toUpperCase()}`))
                    .join(', ')
                : t('location_settings:NONE')}
            </TieringDetailsBody>
          )}
        </Flex>
        <Flex flexDirection="column" padding="40px 0 40px 0">
          <ConflictingShiftsBiddingSubHeader>
            <ConflictingShiftsBiddingHeading>
              {t('location_settings:DEFAULT_OVERTIME_SETTINGS')}
            </ConflictingShiftsBiddingHeading>
          </ConflictingShiftsBiddingSubHeader>
          {isEditMode && canEditDefaultOvertime && (
            <Flex padding="0 0 16px 0">
              <ConflictingShiftsBiddingText>
                {t('location_settings:DEFAULT_OVERTIME_SETTINGS_DESCRIPTION')}
                &nbsp;
              </ConflictingShiftsBiddingText>
            </Flex>
          )}
          {isEditMode && canEditDefaultOvertime ? (
            <Flex flexDirection="column" gap="8px" margin="0 0 32px 0">
              <BKJRadioInput
                name="biddingOvertimeDefault"
                value="true"
                checked={values.biddingOvertimeDefault === true}
                onChange={() =>
                  onChange({ target: { name: 'biddingOvertimeDefault', value: true } })
                }
                label={t('location_settings:ALLOW_OVERTIME_BY_DEFAULT')}
              />
              <BKJRadioInput
                name="biddingOvertimeDefault"
                value="false"
                checked={values.biddingOvertimeDefault === false}
                onChange={() =>
                  onChange({ target: { name: 'biddingOvertimeDefault', value: false } })
                }
                label={t('location_settings:DO_NOT_ALLOW_OVERTIME_BY_DEFAULT')}
              />
            </Flex>
          ) : (
            <TieringDetailsBody>
              {biddingConfiguration?.biddingOvertimeDefault ?? true
                ? t('location_settings:ALLOW_OVERTIME_BY_DEFAULT')
                : t('location_settings:DO_NOT_ALLOW_OVERTIME_BY_DEFAULT')}
            </TieringDetailsBody>
          )}
        </Flex>
      </TieringDetailsWrapper>
      <BKJSideModalFooter gap="8px" flexDirection="row-reverse">
        {isEditMode ? (
          <Fragment>
            <BKJButton
              variant="PurpleSolid"
              width="180px"
              onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
              isLoading={isSubmitting}
            >
              {t('auth:SAVE')}
            </BKJButton>
            <BKJButton variant="PurpleOutlined" width="180px" onClick={() => setIsEditMode(false)}>
              {t('auth:CANCEL')}
            </BKJButton>
          </Fragment>
        ) : (
          <BKJButton variant="PurpleOutlined" width="180px" onClick={() => setIsEditMode(true)}>
            {t('location_settings:EDIT')}
          </BKJButton>
        )}
      </BKJSideModalFooter>
    </Flex>
  );
};

export const ShiftBiddingDetails: FC = () => {
  return (
    <ShiftDistributionLocationDataProvider>
      <ShiftBiddingDetailsFormProvider>
        <ShiftBiddingDetailsViewComponent />
      </ShiftBiddingDetailsFormProvider>
    </ShiftDistributionLocationDataProvider>
  );
};
